import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { BaseLead, LeadDetails } from '@/types/lead';

export class LeadService {
  /**
   * Get all leads with pagination, status filtering, and vehicle type filtering
   * GET /admin/leads?page=1&limit=10&status=active&search=...
   */
  static async getAllLeads(
    page: number = 1,
    limit: number = 10,
    statuses?: string[],
    vehicleType?: string,
    search?: string
  ): Promise<{ data: BaseLead[]; total: number }> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };

    if (statuses?.length) params.status = statuses.join(",");
    if (vehicleType) params.vehicleType = vehicleType;
    if (search) params.search = search;

    const response = await api.get<{ success: true; total: number; data: BaseLead[] }>(
      `/admin/leads`,
      { params }
    );
    
    return {
      data: response.data || [],
      total: response.total || 0,
    };
  }

  /**
   * Get single lead by ID (full details)
   * GET /admin/leads/:id
   */
  static async getLead(leadId: string): Promise<LeadDetails> {
    const response = await api.get<{ success: true; data: LeadDetails }>(
      `/admin/leads/${leadId}`
    );
    
    return response.data as LeadDetails;
  }

  /**
   * Update lead status
   * PATCH /admin/leads/:id/status
   * Frontend should send technical enum values (NEW, CONTACTED, etc.)
   * Note: StatusDropdown already converts Swedish labels to technical enum values
   */
  static async updateLeadStatus(leadId: string, status: string): Promise<void> {
    // Status is already a technical enum value (NEW, CONTACTED, etc.) from StatusDropdown
    await api.patch(`/admin/leads/${leadId}/status`, { status });
  }

  /**
   * Add comment to lead
   * POST /admin/leads/:id/comments
   */
  static async addComment(leadId: string, comment: { text: string; author: string; mentions?: string[] }): Promise<{ success: boolean }> {
    const response = await api.post(`/admin/leads/${leadId}/comments`, { 
      text: comment.text,
      author: comment.author,
      mentions: comment.mentions || [],
    });
    return { success: true };
  }

  static async updateComment(leadId: string, commentId: string, text: string): Promise<void> {
    await api.put(`/admin/leads/${leadId}/comments/${commentId}`, { text });
  }

  static async deleteComment(leadId: string, commentId: string): Promise<void> {
    await api.delete(`/admin/leads/${leadId}/comments/${commentId}`);
  }

  /**
   * Mask lead (hide sensitive information for guest leads)
   * POST /admin/leads/:id/mask
   */
  static async maskLead(leadId: string): Promise<{ success: boolean; data: LeadDetails }> {
    const response = await api.post<{ success: boolean; data: LeadDetails }>(
      `/admin/leads/${leadId}/mask`
    );
    return { success: response.success ?? false, data: response.data };
  }
}
